import CollapseOnEscape from 'slate-collapse-on-escape';
import TrailingBlock from '@wikifactory/slate-trailing-block';
import EditList from 'arrizalamin-slate-edit-list';

import { InsertNodeOnEnter } from 'libs/editor/plugins/helpers/insertNodeOnEnter';

import { Bold } from 'libs/editor/plugins/marks/bold';
import { Italic } from 'libs/editor/plugins/marks/italic';
import { Code } from 'libs/editor/plugins/marks/code';

import { HeaderTwo } from 'libs/editor/plugins/nodes/blocks/headerTwo';
import { HeaderThree } from 'libs/editor/plugins/nodes/blocks/headerThree';
import { HeaderFour } from 'libs/editor/plugins/nodes/blocks/headerFour';

import { Quote } from 'libs/editor/plugins/nodes/blocks/quote';
import { UnorderedList } from 'libs/editor/plugins/nodes/blocks/unorderedList';
import { OrderedList } from 'libs/editor/plugins/nodes/blocks/orderedList';
import { ListItem } from 'libs/editor/plugins/nodes/blocks/listItem';
import { CodeBlock } from 'libs/editor/plugins/nodes/blocks/codeBlock';
import { CodeLine } from 'libs/editor/plugins/nodes/blocks/codeLine';
import { Image } from 'libs/editor/plugins/nodes/inlines/image';
import { ImageBlock } from 'libs/editor/plugins/nodes/blocks/image';
import { Video } from 'libs/editor/plugins/nodes/inlines/video';
import { VideoBlock } from 'libs/editor/plugins/nodes/blocks/video';
import { Preview } from 'libs/editor/plugins/nodes/inlines/preview';
import { PreviewBlock } from 'libs/editor/plugins/nodes/blocks/preview';
import { Table } from 'libs/editor/plugins/nodes/blocks/table';

import { Link } from 'libs/editor/plugins/nodes/inlines/link';
import { LinkifiedLink } from 'libs/editor/plugins/nodes/inlines/linkifiedLink';

import { Paragraph } from 'libs/editor/plugins/nodes/blocks/paragraph';

import { Linkify } from 'libs/editor/plugins/features/linkify';
import { EmbedVideo } from 'libs/editor/plugins/features/video';

const listPlugin = EditList({
  types: ['unordered_list', 'ordered_list'],
  typeItem: 'list_item',
  typeDefault: 'paragraph',
});

const bold = Bold();
const italic = Italic();
const code = Code();

const headerTwo = HeaderTwo();
const headerThree = HeaderThree();
const headerFour = HeaderFour();

const quote = Quote();
const unorderedList = UnorderedList(listPlugin);
const orderedList = OrderedList(listPlugin);
const listItem = ListItem();
const codeBlock = CodeBlock();
const codeLine = CodeLine();
const image = Image();
const imageBlock = ImageBlock();
const video = Video();
const videoBlock = VideoBlock();
const link = Link();
const preview = Preview();
const previewBlock = PreviewBlock();
const linkifiedLink = LinkifiedLink();
const table = Table();

// NOTE: Execution of plugins is order-sensitive, hence "Stack".
const basicPluginStack = {
  buttons: [
    bold.button,
    italic.button,
    code.button,
    link.button,
    'separator',
    unorderedList.button,
    orderedList.button,
  ],
  plugins: [
    ...bold.plugins,
    ...italic.plugins,
    ...code.plugins,
    ...link.plugins,

    ...unorderedList.plugins,
    ...orderedList.plugins,
    ...listItem.plugins,
    ...table.plugins,
  ],
};

const basicPlusButtons = [
  bold.button,
  italic.button,
  code.button,
  link.button,
  // 'separator',
  unorderedList.button,
  orderedList.button,
  quote.button,
];
const basicPlusMediaButtons = [
  previewBlock.uploadButton,
  previewBlock.button,
  imageBlock.button,
  videoBlock.button,
];
const basicPlusPluginStack = {
  buttons: basicPlusButtons,
  mediaButtons: basicPlusMediaButtons,
  plugins: [
    EmbedVideo(),
    Linkify(),
    ...bold.plugins,
    ...italic.plugins,
    ...code.plugins,
    ...link.plugins,
    ...linkifiedLink.plugins,

    ...unorderedList.plugins,
    ...orderedList.plugins,
    ...listItem.plugins,

    ...image.plugins,
    ...imageBlock.plugins,
    ...video.plugins,
    ...videoBlock.plugins,
    ...preview.plugins,
    ...previewBlock.plugins,
    ...table.plugins,
    ...quote.plugins,
  ],
};

const chatPluginStack = {
  buttons: [
    bold.button,
    italic.button,
    code.button,
    link.button,
    // TODO: Once changed the list plugin, uncomment
    // unorderedList.button,
    // orderedList.button,

    quote.button,
  ],
  mediaButtons: basicPlusMediaButtons,
  plugins: [
    EmbedVideo(),
    Linkify(),
    ...bold.plugins,
    ...italic.plugins,
    ...code.plugins,
    ...link.plugins,
    ...linkifiedLink.plugins,

    ...unorderedList.plugins,
    ...orderedList.plugins,
    ...listItem.plugins,

    ...image.plugins,
    ...imageBlock.plugins,
    ...video.plugins,
    ...videoBlock.plugins,
    ...preview.plugins,
    ...previewBlock.plugins,
    ...table.plugins,
    ...quote.plugins,
  ],
};

const advancedPluginStack = {
  buttons: [
    bold.button,
    italic.button,
    code.button,
    link.button,
    'separator',
    headerTwo.button,
    headerThree.button,
    'separator',
    quote.button,
    unorderedList.button,
    orderedList.button,
    codeBlock.button,
    table.button,
  ],
  mediaButtons: [
    previewBlock.uploadButton,
    previewBlock.button,
    imageBlock.button,
    videoBlock.button,
  ],
  plugins: [
    EmbedVideo(),
    Linkify(),
    ...bold.plugins,
    ...italic.plugins,
    ...code.plugins,

    ...headerTwo.plugins,
    ...headerThree.plugins,
    ...headerFour.plugins,

    ...quote.plugins,
    ...unorderedList.plugins,
    ...orderedList.plugins,
    ...listItem.plugins,
    ...codeBlock.plugins,
    ...codeLine.plugins,
    ...link.plugins,
    ...linkifiedLink.plugins,
    ...image.plugins,
    ...imageBlock.plugins,
    ...video.plugins,
    ...videoBlock.plugins,
    ...preview.plugins,
    ...previewBlock.plugins,
    ...table.plugins,
  ],
};

const markdownPluginStack = {
  buttons: [
    bold.button,
    italic.button,
    code.button,
    link.button,
    'separator',
    headerThree.button,
    headerFour.button,
    'separator',
    quote.button,
    unorderedList.button,
    orderedList.button,
    codeBlock.button,
  ],
  mediaButtons: [
    previewBlock.uploadButton,
    previewBlock.button,
    imageBlock.button,
    videoBlock.button,
    table.button,
  ],
  plugins: [
    EmbedVideo(),
    Linkify(),
    ...bold.plugins,
    ...italic.plugins,
    ...code.plugins,

    ...headerThree.plugins,
    ...headerFour.plugins,

    ...quote.plugins,
    ...unorderedList.plugins,
    ...orderedList.plugins,
    ...listItem.plugins,
    ...codeBlock.plugins,
    ...codeLine.plugins,
    ...link.plugins,
    ...linkifiedLink.plugins,
    ...image.plugins,
    ...imageBlock.plugins,
    ...video.plugins,
    ...videoBlock.plugins,
    ...preview.plugins,
    ...previewBlock.plugins,
    ...table.plugins,
  ],
};

const corePluginStack = [
  InsertNodeOnEnter({
    type: 'paragraph',
    onTypes: [
      'header_one',
      'header_two',
      'header_three',
      'header_four',
      'link',
      'blockquote',
    ],
  }),
  CollapseOnEscape({ toEdge: 'end' }),
  Paragraph(),
  TrailingBlock({
    type: 'paragraph',
    match: lastNode =>
      lastNode.type === 'paragraph' &&
      lastNode.nodes.size === 1 &&
      lastNode.nodes.get(0).object === 'text' &&
      !lastNode.text,
  }),
];

export const pluginPresets = {
  markdown: [...markdownPluginStack.plugins, ...corePluginStack],
  advanced: [...advancedPluginStack.plugins, ...corePluginStack],
  basicPlus: [...basicPlusPluginStack.plugins, ...corePluginStack],
  basic: [...basicPluginStack.plugins, ...corePluginStack],
  none: [...corePluginStack],
  chat: [
    ...chatPluginStack.plugins,
    ...corePluginStack.slice(0, 2),
    TrailingBlock({
      type: 'paragraph',
      match: lastNode =>
        lastNode.type === 'paragraph' && lastNode.nodes.size === 1,
    }),
  ],
};

export const buttonPresets = {
  markdown: [...markdownPluginStack.buttons],
  advanced: [...advancedPluginStack.buttons],
  basicPlus: [...basicPlusPluginStack.buttons],
  basic: [...basicPluginStack.buttons],
  none: [],
  chat: [...chatPluginStack.buttons],
};

export const mediaPresets = {
  markdown: [...markdownPluginStack.mediaButtons],
  advanced: [...advancedPluginStack.mediaButtons],
  basicPlus: [...basicPlusPluginStack.mediaButtons],
  basic: [],
  none: [],
  chat: [...chatPluginStack.mediaButtons],
};
